import {
	faBuilding,
	faListCheck,
	faPenLine,
	faScaleBalanced,
} from "@fortawesome/pro-regular-svg-icons";

import { faUsers } from "@fortawesome/pro-solid-svg-icons";

import i18n from "@/i18n";
import { ROUTE_CONFIG } from "@/routes/config";

import { Icon } from "@/components";
import { PermissionRoles } from "@/enum";

import { SvgIcon } from "@/components/Icon/SvgIcon";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";
import { ReactComponent as ToDos } from "@assets/icons/sidebar/todos.svg?react";
import { ReactComponent as Clipboard } from "@assets/icons/sidebar/clipboard.svg?react";
import { ReactComponent as Settings } from "@assets/icons/sidebar/settings.svg?react";
import { ReactComponent as Industry } from "@assets/icons/sidebar/industry.svg?react";
import { ReactComponent as CompanySize } from "@assets/icons/sidebar/company-size.svg?react";
import { ReactComponent as CompanyRevenue } from "@assets/icons/sidebar/company-revenue.svg?react";

export interface SidenavItem {
	title: string;
	path: string;
	onClick?: () => void;
	strictPath?: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	icon?: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	iconActive?: any;
	type?: string;
	permission?: PermissionRoles;
	companyIds?: number[];
	additionalTextClassName?: string;
	additionalContainerClassName?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	iconRight?: any;
	isHidden?: boolean;
}

interface NavItemInner {
	upper: SidenavItem[];
	middle: SidenavItem[];
	secondMiddle?: SidenavItem[];
	lower: SidenavItem[];
}

interface NavItemConfig {
	ALL: NavItemInner;
}

export const useNavigationGetter = (): NavItemConfig => {
	const { user } = useAppSelector(authSelector);

	return {
		ALL: {
			upper: [
				{
					title: i18n.t("sidebar.to-do-list"),
					path: ROUTE_CONFIG.TO_DO_LIST,
					icon: (
						<SvgIcon className="w-[24px] h-[24px] fill-primary-blue-dark" svgIcon={Clipboard} />
					),
					iconActive: <SvgIcon className="w-[24px] h-[24px] fill-white" svgIcon={Clipboard} />,
					type: "icon",
					permission: PermissionRoles.TO_DO_LIST,
					strictPath: true,
				},
			],
			middle: [
				{
					title: i18n.t("sidebar.manage-to-do"),
					path: `${ROUTE_CONFIG.MANAGE_TO_DO}`,
					icon: <Icon className="w-[24px] h-[24px] text-primary-blue-dark" icon={faPenLine} />,
					iconActive: <Icon className="w-[24px] h-[24px] text-white" icon={faPenLine} />,
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
					strictPath: true,
				},
				{
					title: i18n.t("sidebar.manage-questionnaire"),
					path: ROUTE_CONFIG.MANAGE_QUESTIONNAIRE,
					icon: <SvgIcon className="w-[24px] h-[24px] fill-primary-blue-dark" svgIcon={ToDos} />,
					iconActive: <SvgIcon className="w-[24px] h-[24px] fill-white" svgIcon={ToDos} />,
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
				},
				{
					title: i18n.t("sidebar.manage-industries"),
					path: ROUTE_CONFIG.MANAGE_INDUSTRIES,
					icon: <SvgIcon className="w-[24px] h-[24px] fill-primary-blue-dark" svgIcon={Industry} />,
					iconActive: <SvgIcon className="w-[24px] h-[24px] fill-white" svgIcon={Industry} />,
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
				},
				{
					title: i18n.t("sidebar.manage-company-size"),
					path: ROUTE_CONFIG.MANAGE_COMPANY_SIZE,
					icon: (
						<SvgIcon className="w-[24px] h-[24px] fill-primary-blue-dark" svgIcon={CompanySize} />
					),
					iconActive: <SvgIcon className="w-[24px] h-[24px] fill-white" svgIcon={CompanySize} />,
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
				},
				{
					title: i18n.t("sidebar.manage-company-revenue"),
					path: ROUTE_CONFIG.MANAGE_COMPANY_REVENUE,
					icon: (
						<SvgIcon
							className="w-[24px] h-[24px] fill-primary-blue-dark"
							svgIcon={CompanyRevenue}
						/>
					),
					iconActive: <SvgIcon className="w-[24px] h-[24px] fill-white" svgIcon={CompanyRevenue} />,
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
				},
				{
					title: i18n.t("sidebar.manage-company-balance"),
					path: ROUTE_CONFIG.MANAGE_COMPANY_BALANCE,
					icon: <Icon icon={faScaleBalanced} />,
					iconActive: <SvgIcon className="w-[24px] h-[24px] fill-white" svgIcon={CompanyRevenue} />,
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
				},
				{
					title: i18n.t("sidebar.manage-task-templates"),
					path: ROUTE_CONFIG.MANAGE_TASK_TEMPLATES,
					icon: <Icon className="w-[24px] h-[24px] text-primary-blue-dark" icon={faListCheck} />,
					iconActive: <Icon className="w-[24px] h-[24px] text-white" icon={faListCheck} />,
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
				},
			],
			lower: [
				{
					title: i18n.t("sidebar.settings"),
					path: ROUTE_CONFIG.ACCOUNT,
					icon: <SvgIcon className="w-[24px] h-[24px] fill-primary-blue-dark" svgIcon={Settings} />,
					iconActive: <SvgIcon className="w-[24px] h-[24px] fill-white" svgIcon={Settings} />,
					type: "icon",
					permission: PermissionRoles.USER,
				},
				{
					title: i18n.t("sidebar.users"),
					path: `${ROUTE_CONFIG.COMPANIES}/${user?.company?.id}`,
					icon: <Icon className="w-[24px] h-[24px] text-primary-blue-dark" icon={faUsers} />,
					iconActive: <Icon className="w-[24px] h-[24px] text-white" icon={faUsers} />,
					type: "icon",
					permission: PermissionRoles.UPDATE_USER,
				},
				{
					title: i18n.t("sidebar.companies"),
					path: ROUTE_CONFIG.COMPANIES,
					icon: <Icon className="w-[24px] h-[24px] text-primary-blue-dark" icon={faBuilding} />,
					iconActive: <Icon className="w-[24px] h-[24px] text-white" icon={faBuilding} />,
					type: "icon",
					permission: PermissionRoles.GLOBAL_ADMIN,
				},
			],
		},
	};
};
